// ApprovalQueueSetupPage.js

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import SecurityIcon from "@mui/icons-material/Security";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { API_URL } from "./config";

const ApprovalQueueSetupPage = () => {
  const token = localStorage.getItem("auth") || localStorage.getItem("token") || "";

  const ORANGE = "#F07828";
  const ORANGE_2 = "#ed7f35ff";
  const BLUE = "#1565C0";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.04)";
  const ROW_HOVER = "rgba(25,118,210,0.08)";

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const pageWrapSx = { p: 2, minHeight: "100vh", backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${ORANGE} 0%, ${ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: BORDER },
      "&:hover fieldset": { borderColor: BORDER_HOVER },
      "&.Mui-focused fieldset": { borderColor: BORDER_FOCUS },
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: ORANGE,
    "&:hover": { backgroundColor: ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const [queues, setQueues] = useState([]);
  const [roles, setRoles] = useState([]);
  const [queueCode, setQueueCode] = useState("AIDEVELOPER");
  const [queueConfig, setQueueConfig] = useState({
    QueueName: "",
    IsActive: 1,
    AllowEmergencyOverride: 1,
  });
  const [steps, setSteps] = useState([]);

  const [roleSearch, setRoleSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const availableRoles = useMemo(() => {
    const selected = new Set(steps.map((s) => Number(s.RoleID)));
    const q = roleSearch.trim().toLowerCase();

    return roles.filter((r) => {
      if (selected.has(Number(r.RoleID))) return false;
      if (!q) return true;
      return (
        String(r.RoleName || "").toLowerCase().includes(q) ||
        String(r.UserType || "").toLowerCase().includes(q)
      );
    });
  }, [roles, steps, roleSearch]);

  const activeSteps = useMemo(
    () => steps.filter((s) => Number(s.IsActive) === 1),
    [steps]
  );

  const overrideSteps = useMemo(
    () => steps.filter((s) => Number(s.CanOverride) === 1),
    [steps]
  );

  const fetchQueues = async () => {
    const res = await axios.get(`${API_URL}/api/approval-queues/queues`, {
      headers: authHeaders,
    });
    setQueues(Array.isArray(res.data) ? res.data : []);
  };

  const fetchRoles = async () => {
    const res = await axios.get(`${API_URL}/api/approval-queues/roles`, {
      headers: authHeaders,
    });
    setRoles(Array.isArray(res.data) ? res.data : []);
  };

  const fetchQueueSetup = async (code = queueCode) => {
    setLoading(true);
    setError("");

    try {
      const res = await axios.get(`${API_URL}/api/approval-queues/${code}`, {
        headers: authHeaders,
      });

      const cfg = res.data?.config || {};
      const apiSteps = Array.isArray(res.data?.steps) ? res.data.steps : [];

      setQueueConfig({
        QueueName: cfg.QueueName || "",
        IsActive: Number(cfg.IsActive ?? 1),
        AllowEmergencyOverride: Number(cfg.AllowEmergencyOverride ?? 1),
      });

      setSteps(
        apiSteps.map((s, idx) => ({
          QueueStepId: s.QueueStepId,
          RoleID: Number(s.RoleID),
          RoleName: s.RoleName,
          Priority: s.Priority,
          UserType: s.UserType,
          StepOrder: Number(s.StepOrder || idx + 1),
          IsRequired: Number(s.IsRequired ?? 1),
          IsActive: Number(s.IsActive ?? 1),
          CanOverride: Number(s.CanOverride ?? 0),
          AppliesTo: s.AppliesTo,
        }))
      );
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load approval queue setup");
      setSteps([]);
    } finally {
      setLoading(false);
    }
  };

  const boot = async () => {
    setLoading(true);
    setError("");

    try {
      await Promise.all([fetchQueues(), fetchRoles()]);
      await fetchQueueSetup(queueCode);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load page");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token || !queueCode) return;
    fetchQueueSetup(queueCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueCode]);

  const addRoleToQueue = (role) => {
    setSteps((prev) => [
      ...prev,
      {
        RoleID: Number(role.RoleID),
        RoleName: role.RoleName,
        Priority: role.Priority,
        UserType: role.UserType,
        StepOrder: prev.length + 1,
        IsRequired: 1,
        IsActive: 1,
        CanOverride: 0,
        AppliesTo: "ALL",
      },
    ]);
  };

  const toggleAppliesTo = (roleId) => {
    setSteps((prev) =>
      prev.map((s) =>
        Number(s.RoleID) === Number(roleId)
          ? {
              ...s,
              AppliesTo:
                s.AppliesTo === "ALL" ? "HOSTELLER_ONLY" : "ALL",
            }
          : s
      )
    );
  };

  const removeRole = (roleId) => {
    setSteps((prev) =>
      prev
        .filter((s) => Number(s.RoleID) !== Number(roleId))
        .map((s, idx) => ({ ...s, StepOrder: idx + 1 }))
    );
  };

  const moveStep = (index, direction) => {
    setSteps((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;

      [next[index], next[target]] = [next[target], next[index]];

      return next.map((s, idx) => ({
        ...s,
        StepOrder: idx + 1,
      }));
    });
  };

  const toggleStepField = (roleId, field) => {
    setSteps((prev) =>
      prev.map((s) =>
        Number(s.RoleID) === Number(roleId)
          ? { ...s, [field]: Number(s[field]) === 1 ? 0 : 1 }
          : s
      )
    );
  };

  const saveQueue = async () => {
    if (queueCode === "AIDEVELOPER" && steps.some(s => s.AppliesTo !== "ALL")) {
      setError("AI Developer approval steps must use Applies To = ALL");
      return;
    }
    if (!queueCode) {
      setError("Please select a queue");
      return;
    }

    if (!steps.length) {
      setError("Please add at least one role to the approval queue");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = {
        QueueName:
          queueConfig.QueueName ||
          queues.find((q) => q.QueueCode === queueCode)?.QueueName ||
          `${queueCode} Approval Queue`,
        IsActive: Number(queueConfig.IsActive),
        AllowEmergencyOverride: Number(queueConfig.AllowEmergencyOverride),
        Steps: steps.map((s, idx) => ({
          RoleID: Number(s.RoleID),
          StepOrder: idx + 1,
          IsRequired: Number(s.IsRequired),
          IsActive: Number(s.IsActive),
          CanOverride: Number(s.CanOverride),
          AppliesTo: s.AppliesTo === "ALL" ? "ALL" : "HOSTELLER_ONLY",
        })),
      };

      await axios.post(`${API_URL}/api/approval-queues/${queueCode}`, payload, {
        headers: authHeaders,
      });

      setSuccess("Approval queue saved successfully");
      await fetchQueueSetup(queueCode);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save approval queue");
    } finally {
      setSaving(false);
    }
  };

  const QueueStat = ({ icon, label, value, helper }) => (
    <Paper
      elevation={0}
      sx={{
        ...surfaceSx,
        p: 1.5,
        backgroundColor: "rgba(25,118,210,0.025)",
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box sx={{ color: ORANGE, display: "flex" }}>{icon}</Box>
        <Box>
          <Typography sx={{ color: "text.secondary", fontSize: "0.78rem", fontWeight: 800 }}>
            {label}
          </Typography>
          <Typography sx={{ color: BLUE, fontSize: "1.25rem", fontWeight: 900 }}>
            {value}
          </Typography>
          {helper ? (
            <Typography sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.15rem", fontWeight: 900 }}>
              Approval Queue Setup
            </Typography>
            <Typography sx={{ opacity: 0.92, fontSize: "0.86rem" }}>
              Configure role-wise approval flow for time off, admissions, and emergency overrides.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              sx={{
                ...outlinedBtnSx,
                color: "white",
                borderColor: "rgba(255,255,255,0.75)",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.12)",
                },
              }}
              startIcon={<RefreshIcon />}
              onClick={boot}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              sx={{
                ...primaryBtnSx,
                backgroundColor: "white",
                color: ORANGE,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.92)" },
              }}
              startIcon={<SaveIcon />}
              disabled={saving}
              onClick={saveQueue}
            >
              {saving ? "Saving..." : "Save Queue"}
            </Button>
          </Stack>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess("")}>
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              {success}
            </Alert>
          </Snackbar>

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <QueueStat
                icon={<PlaylistAddCheckIcon />}
                label="Queue Steps"
                value={steps.length}
                helper="Total selected roles"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <QueueStat
                icon={<SecurityIcon />}
                label="Active Steps"
                value={activeSteps.length}
                helper="Currently active"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <QueueStat
                icon={<WarningAmberIcon />}
                label="Override Roles"
                value={overrideSteps.length}
                helper="Emergency approvers"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <QueueStat
                icon={<SaveIcon />}
                label="Queue"
                value={queueCode || "-"}
                helper={queueConfig.IsActive ? "Active" : "Inactive"}
              />
            </Grid>
          </Grid>

          <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
            <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1.5 }}>
              Select Approval Queue
            </Typography>

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Approval Queue"
                  value={queueCode}
                  onChange={(e) => setQueueCode(e.target.value)}
                  sx={inputSx}
                >
                  {queues.map((q) => (
                    <MenuItem key={q.QueueCode} value={q.QueueCode}>
                      {q.QueueName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Queue Display Name"
                  value={queueConfig.QueueName}
                  onChange={(e) =>
                    setQueueConfig((p) => ({ ...p, QueueName: e.target.value }))
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Queue Status"
                  value={queueConfig.IsActive}
                  onChange={(e) =>
                    setQueueConfig((p) => ({ ...p, IsActive: Number(e.target.value) }))
                  }
                  sx={inputSx}
                >
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={0}>Inactive</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Emergency Override"
                  value={queueConfig.AllowEmergencyOverride}
                  onChange={(e) =>
                    setQueueConfig((p) => ({
                      ...p,
                      AllowEmergencyOverride: Number(e.target.value),
                    }))
                  }
                  sx={inputSx}
                >
                  <MenuItem value={1}>Allowed</MenuItem>
                  <MenuItem value={0}>Disabled</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ ...surfaceSx, overflow: "hidden" }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    backgroundColor: "rgba(25,118,210,0.05)",
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                >
                  <Typography sx={{ fontWeight: 900, color: BLUE }}>
                    Available Roles
                  </Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: "0.82rem" }}>
                    Click Add to include the role in the approval flow.
                  </Typography>
                </Box>

                <Box sx={{ p: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search roles"
                    value={roleSearch}
                    onChange={(e) => setRoleSearch(e.target.value)}
                    sx={{ ...inputSx, mb: 1.5 }}
                  />

                  <Stack spacing={1} sx={{ maxHeight: 520, overflow: "auto", pr: 0.5 }}>
                    {availableRoles.map((role) => (
                      <Paper
                        key={role.RoleID}
                        elevation={0}
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          border: `1px solid ${BORDER}`,
                          backgroundColor: "rgba(25,118,210,0.025)",
                          "&:hover": { backgroundColor: ROW_HOVER },
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={1}
                        >
                          <Box>
                            <Typography sx={{ fontWeight: 900 }}>
                              {role.RoleName}
                            </Typography>
                            <Typography sx={{ color: "text.secondary", fontSize: "0.78rem" }}>
                            </Typography>
                          </Box>

                          <Button
                            size="small"
                            variant="outlined"
                            sx={outlinedBtnSx}
                            onClick={() => addRoleToQueue(role)}
                          >
                            Add
                          </Button>
                        </Stack>
                      </Paper>
                    ))}

                    {!availableRoles.length ? (
                      <Typography sx={{ color: "text.secondary" }}>
                        No roles available.
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ ...surfaceSx, overflow: "hidden" }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    backgroundColor: "rgba(25,118,210,0.05)",
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                >
                  <Typography sx={{ fontWeight: 900, color: BLUE }}>
                    Approval Flow Order
                  </Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: "0.82rem" }}>
                    Arrange roles in the order they should approve. Enable override for emergency approvers.
                  </Typography>
                </Box>

                <Box sx={{ p: 2 }}>
                  {loading ? (
                    <Typography sx={{ color: "text.secondary" }}>
                      Loading queue setup...
                    </Typography>
                  ) : !steps.length ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: `1px dashed ${BORDER}`,
                        textAlign: "center",
                        backgroundColor: "rgba(25,118,210,0.025)",
                      }}
                    >
                      <Typography sx={{ fontWeight: 900, color: BLUE }}>
                        No approval roles selected
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }}>
                        Add roles from the left side to build this queue.
                      </Typography>
                    </Paper>
                  ) : (
                    <Stack spacing={1}>
                      {steps.map((step, idx) => (
                        <Paper
                          key={step.RoleID}
                          elevation={0}
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            border: `1px solid ${BORDER}`,
                            backgroundColor: idx % 2 === 0 ? ROW_ALT : "#fff",
                            "&:hover": { backgroundColor: ROW_HOVER },
                          }}
                        >
                          <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} md={1}>
                              <Chip
                                label={idx + 1}
                                sx={{
                                  fontWeight: 900,
                                  backgroundColor: ORANGE,
                                  color: "white",
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={2.5}>
                              <Typography sx={{ fontWeight: 900 }}>
                                {step.RoleName}
                              </Typography>
                              <Typography sx={{ color: "text.secondary", fontSize: "0.78rem" }}>
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={5.5}>
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Chip
                                  size="small"
                                  label={step.IsActive ? "Active" : "Inactive"}
                                  color={step.IsActive ? "success" : "default"}
                                  onClick={() => toggleStepField(step.RoleID, "IsActive")}
                                />

                                <Chip
                                  size="small"
                                  label={step.IsRequired ? "Required" : "Optional"}
                                  color={step.IsRequired ? "primary" : "default"}
                                  onClick={() => toggleStepField(step.RoleID, "IsRequired")}
                                />

                                <Chip
                                  size="small"
                                  icon={<WarningAmberIcon />}
                                  label={step.CanOverride ? "Can Override" : "No Override"}
                                  color={step.CanOverride ? "warning" : "default"}
                                  onClick={() => toggleStepField(step.RoleID, "CanOverride")}
                                />

                                <Chip
                                  size="small"
                                  label={step.AppliesTo === "ALL" ? "All Students" : "Hostellers Only"}
                                  color={step.AppliesTo === "ALL" ? "success" : "default"}
                                  onClick={() => toggleAppliesTo(step.RoleID)}
                                />
                              </Stack>

                              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <Checkbox
                                  size="small"
                                  checked={Number(step.IsActive) === 1}
                                  onChange={() => toggleStepField(step.RoleID, "IsActive")}
                                />
                                <Typography sx={{ fontSize: "0.82rem", mt: 0.6 }}>Active</Typography>

                                <Checkbox
                                  size="small"
                                  checked={Number(step.IsRequired) === 1}
                                  onChange={() => toggleStepField(step.RoleID, "IsRequired")}
                                />
                                <Typography sx={{ fontSize: "0.82rem", mt: 0.6 }}>Required</Typography>

                                <Checkbox
                                  size="small"
                                  checked={Number(step.CanOverride) === 1}
                                  onChange={() => toggleStepField(step.RoleID, "CanOverride")}
                                />
                                <Typography sx={{ fontSize: "0.82rem", mt: 0.6 }}>Override</Typography>

                                <Checkbox
                                  size="small"
                                  checked={step.AppliesTo === "ALL"}
                                  onChange={() => toggleAppliesTo(step.RoleID)}
                                />
                                <Typography sx={{ fontSize: "0.82rem", mt: 0.6 }}>Applies to All</Typography>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} md={3}>
                              <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                                <IconButton
                                  size="small"
                                  disabled={idx === 0}
                                  onClick={() => moveStep(idx, -1)}
                                  sx={{ color: BLUE }}
                                >
                                  <ArrowUpwardIcon fontSize="small" />
                                </IconButton>

                                <IconButton
                                  size="small"
                                  disabled={idx === steps.length - 1}
                                  onClick={() => moveStep(idx, 1)}
                                  sx={{ color: BLUE }}
                                >
                                  <ArrowDownwardIcon fontSize="small" />
                                </IconButton>

                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    ...outlinedBtnSx,
                                    color: "#C62828",
                                    borderColor: "rgba(198,40,40,0.35)",
                                  }}
                                  onClick={() => removeRole(step.RoleID)}
                                >
                                  Remove
                                </Button>
                              </Stack>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Stack>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      sx={outlinedBtnSx}
                      startIcon={<RefreshIcon />}
                      onClick={() => fetchQueueSetup(queueCode)}
                    >
                      Reset
                    </Button>

                    <Button
                      variant="contained"
                      sx={primaryBtnSx}
                      startIcon={<SaveIcon />}
                      disabled={saving}
                      onClick={saveQueue}
                    >
                      {saving ? "Saving..." : "Save Approval Flow"}
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default ApprovalQueueSetupPage;